import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { AuthContext } from '../context/auth-context';
import { projectActions, writeStoredProject } from '../../store/project';
import ProjectsAPI from '../../services/api/admin/projects-api';
import Logger from '../../utils/logger';

const DRAWER_WIDTH = 400;

const ProjectSelectionDrawer = () => {
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const isDrawerOpen = useSelector((state) => state.project.isDrawerOpen);

  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    dispatch(projectActions.closeProjectDrawer());
  };

  // Fetch the active project list each time the drawer opens.
  useEffect(() => {
    if (!isDrawerOpen || !auth.token) return;

    let cancelled = false;
    const fetchProjects = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await ProjectsAPI.getActive(auth.token);
        if (!cancelled) {
          // Order by job number (natural/numeric sort so 2 < 10).
          const sorted = [...(data || [])].sort((a, b) =>
            String(a.number ?? '').localeCompare(
              String(b.number ?? ''),
              undefined,
              {
                numeric: true,
                sensitivity: 'base',
              }
            )
          );
          setProjects(sorted);
        }
      } catch (err) {
        if (!cancelled) {
          Logger.error('Error loading projects for selection drawer', err);
          setError('Failed to load projects. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    // Reset search each time the drawer opens for a clean start.
    setSearchTerm('');
    fetchProjects();

    return () => {
      cancelled = true;
    };
  }, [isDrawerOpen, auth.token]);

  // Client-side filter on project name OR number, case-insensitive.
  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) => {
      const name = String(project.name || '').toLowerCase();
      const number = String(project.number || '').toLowerCase();
      return name.includes(term) || number.includes(term);
    });
  }, [projects, searchTerm]);

  const handleSelectProject = (project) => {
    const payload = {
      id: project.id,
      number: project.number,
      name: project.name,
      default_trade_id: project.default_trade_id,
    };
    dispatch(projectActions.selectProject(payload));
    writeStoredProject(auth.userId, payload);
    Logger.userAction('Global project selected', { projectId: project.id });
    handleClose();
  };

  return (
    <Drawer
      anchor="left"
      open={isDrawerOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Select Project
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close project selection">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 3, py: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search projects by name or number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Project list */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && error && (
          <Box sx={{ px: 1, py: 4, textAlign: 'center' }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && filteredProjects.length === 0 && (
          <Box sx={{ px: 1, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {projects.length === 0
                ? 'No active projects available.'
                : 'No projects match your search.'}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && filteredProjects.length > 0 && (
          <List sx={{ pt: 1 }}>
            {filteredProjects.map((project) => (
              <Tooltip
                key={project.id}
                title={`${project.number} — ${project.name}`}
                placement="right"
                arrow
                enterDelay={400}
              >
                <ListItemButton
                  onClick={() => handleSelectProject(project)}
                  sx={{
                    p: 0,
                    mb: 2,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    boxShadow: 2,
                    minHeight: 100,
                    display: 'flex',
                    alignItems: 'stretch',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  {/* Colored image block (future home of project images) */}
                  <Box
                    sx={{
                      width: 80,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2,
                      py: 2.5,
                      backgroundColor: '#94a3b8', // slate-400
                    }}
                  >
                    <Box
                      component="img"
                      loading="lazy"
                      src="https://cdn.builder.io/api/v1/image/assets/TEMP/8e55deff970ca50f96b1213712e0a9535b956620ea9f3e733d1865c1873c5a3d?placeholderIfAbsent=true&apiKey=a18dbcc5f1294887aa16ecf9b3731919"
                      alt="Project"
                      sx={{ width: 32, objectFit: 'contain' }}
                    />
                  </Box>

                  {/* Number + name */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: 'grey.900',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {project.number}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {project.name}
                    </Typography>
                  </Box>

                  {/* Chevron */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pr: 1.5,
                      color: 'text.secondary',
                    }}
                  >
                    <ChevronRightIcon />
                  </Box>
                </ListItemButton>
              </Tooltip>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default ProjectSelectionDrawer;
